import React, { useState, useMemo } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Organization } from 'apps/marsinet/src/app/typescript-axios';
import { useTranslation } from 'react-i18next';

interface OrganizationMultiselectProps {
  organizations: Organization[];
  value: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder?: string;
  maxSelectedLabels?: number;
  className?: string;
}

const OrganizationMultiselect: React.FC<OrganizationMultiselectProps> = ({
  organizations,
  value,
  onChange,
  placeholder = 'Seleccionar organizaciones',
  maxSelectedLabels = 3,
  className = 'w-full',
}) => {
  const { t } = useTranslation();
  const [filterField, setFilterField] = useState<string>('name');
  const [searchOrganization, setSearchOrganization] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');

  const organizationOptionTemplate = (option: any) => (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{option.name}</span>
      <span className="text-xs text-gray-500">
        ({t(`roles.${option.role}`)})
      </span>
    </div>
  );

  const getAvailableRoles = () => {
    const roles = new Set<string>();
    organizations.forEach((org) => {
      if (org.role) {
        roles.add(org.role);
      }
    });
    return Array.from(['all_roles', ...roles]).sort();
  };

  const filteredOrganizations = useMemo(() => {
    let filtered = organizations;

    if (filterField === 'role' && selectedRole === 'all_roles') {
      filtered = organizations;
    } else if (filterField === 'role' && selectedRole) {
      filtered = filtered.filter((org) => org.role === selectedRole);
    }

    if (searchOrganization.trim()) {
      filtered = filtered.filter((org) => {
        const searchLower = searchOrganization.toLowerCase();
        return org.name.toLowerCase().includes(searchLower);
      });
    }

    // Always include currently-selected orgs so MultiSelect can render their labels,
    // even when they don't match the current filter.
    const filteredIdSet = new Set(filtered.map((o) => o.o_id));
    const selectedButHidden = organizations.filter(
      (org) => value.includes(org.o_id) && !filteredIdSet.has(org.o_id)
    );

    return [...filtered, ...selectedButHidden];
  }, [organizations, filterField, selectedRole, searchOrganization, value]);

  const filteredIds = useMemo(
    () => filteredOrganizations.map((org) => org.o_id),
    [filteredOrganizations]
  );

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => value.includes(id));

  const handleToggleSelectAll = () => {
    if (allFilteredSelected) {
      onChange(value.filter((id) => !filteredIds.includes(id)));
    } else {
      onChange(Array.from(new Set([...value, ...filteredIds])));
    }
  };

  return (
    <MultiSelect
      value={value}
      options={filteredOrganizations}
      onChange={(e) => onChange(e.value)}
      optionLabel="name"
      optionValue="o_id"
      filter
      filterBy={filterField}
      placeholder={placeholder}
      maxSelectedLabels={maxSelectedLabels}
      itemTemplate={organizationOptionTemplate}
      panelHeaderTemplate={() => (
        <div className="p-3 border-b-1 border-surface-border grid gap-3">
          <InputText
            value={searchOrganization}
            onChange={(e) => setSearchOrganization(e.target.value)}
            placeholder={t('pages.surveys.search_organizations')}
            className="w-full"
            style={{ fontSize: '0.875rem' }}
          />
          <div className="flex items-center justify-between gap-2 align-items-center">
            <Dropdown
              value={selectedRole}
              options={getAvailableRoles().map((role) => ({
                label: t(`roles.${role}`),
                value: role,
              }))}
              onChange={(e) => {
                setFilterField('role');
                setSearchOrganization('');
                e.originalEvent.stopPropagation();
                setSelectedRole(e.value);
              }}
              placeholder={t('pages.surveys.select_role')}
              className="w-40"
              style={{ fontSize: '0.875rem' }}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: '0.7rem', paddingTop: '0.7rem' }}
          >
            <Checkbox
              inputId="org-select-all"
              checked={allFilteredSelected}
              onChange={handleToggleSelectAll}
              onMouseDown={(e) => e.stopPropagation()}
            />
            <label
              htmlFor="org-select-all"
              className="text-sm text-gray-600 cursor-pointer"
              style={{ margin: 0 }}
            >
              {allFilteredSelected
                ? t('pages.surveys.deselect_all')
                : t('pages.surveys.select_all')}
            </label>
          </div>
        </div>
      )}
      className={className}
    />
  );
};

export default OrganizationMultiselect;